import clsx from 'clsx';
import React from 'react';
import UserIcon from '../../assets/icons/user-icon.svg?react';
import Select from '../Select';
import Button from '../Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import Input from '../Input';
import DatePicker from '../DatePicker';
import { User } from '@EHSNavigator/backend';
import moment from 'moment';
import { useLocalStorage } from '@uidotdev/usehooks';
import { UserWithOrgContext } from '../../types/user';

interface ChangeUserRoleModalProps {
  otherClasses?: string;
  handleClose: () => void;
  formHandler: any;
  roles: {
    label: string;
    value: string;
  }[];
  departments: {
    label: string;
    value: string;
  }[];
  initialValues: any;
  editValues: User | undefined;
}

const ChangeUserRoleModal: React.FC<ChangeUserRoleModalProps> = ({
  otherClasses,
  handleClose,
  roles,
  departments,
  formHandler,
  initialValues,
  editValues,
}) => {
  const changeUserRoleModalClasses = clsx(otherClasses);

  const keys = Object.keys(initialValues);
  const hasChanged = keys.some(
    (key) =>
      formHandler?.values?.[key] !==
      initialValues[key as keyof typeof initialValues],
  );

  let updatedDepartments = [...departments];
  const exists = departments?.some((node) => node.value === editValues?.department?.id);

  if (!exists && editValues?.department?.id) {
    updatedDepartments.push({
      label: editValues?.department?.name || '',
      value: editValues?.department?.id,
    });
  }

  const [userInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const isOrganizationOwner = userInfo?.orgContext?.id === initialValues?.id;
  const isCurrentUser = userInfo?.user?.id === initialValues?.id || isOrganizationOwner;
  const isOwner = userInfo?.user?.role?.name === 'Owner';
  const isAdmin = userInfo?.user?.role?.name === 'Admin';

  return (
    <div
      className={changeUserRoleModalClasses}
      data-testid="change-user-role-modal"
    >
      <div className="mb-10 flex w-full flex-col items-center gap-2.5 text-center">
        <UserIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
        <h4 className="text-brand-byzantine">Change User Role</h4>
      </div>
      <form action="#" onSubmit={formHandler?.handleSubmit}>
        {((isCurrentUser && !isOwner) ||
          (!isCurrentUser && isOwner) ||
          (!isCurrentUser && !isOwner)) && (
          <div
            className={clsx(
              'grid gap-6',

             ( ((isCurrentUser && !isOwner) ||
                (!isCurrentUser && isOwner) ||
                (!isCurrentUser && !isOwner)) && !isAdmin)
                ? 'grid-cols-2'
                : 'grid-cols-1',
            )}
          >
            <div className="flex w-full flex-col gap-4">
              <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-lg leading-6 font-bold">
                Internal Details
              </h6>
              <Input
                onBlur={formHandler?.handleBlur}
                label="Employee ID*"
                errorMessage={
                  formHandler?.touched.employeeId &&
                  formHandler?.errors.employeeId
                    ? formHandler?.errors.employeeId
                    : ''
                }
                disabled={formHandler?.isSubmitting}
                value={String(formHandler?.values.employeeId)}
                type="text"
                placeholder="Write something here..."
                onChange={formHandler?.handleChange}
                name="employeeId"
                otherClasses={{ container: 'w-full' }}
              />
            </div>
            {(((isCurrentUser && !isOwner) ||
              (!isCurrentUser && isOwner) ||
              (!isCurrentUser && !isOwner)) && !isAdmin) && (
              <div className="flex w-full flex-col gap-4">
                <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-lg leading-6 font-bold">
                  Employee Role
                </h6>
                <Select
                  label="Role*"
                  list={roles}
                  value={String(formHandler.values.roleId)}
                  disabled={formHandler.isSubmitting}
                  placeholder="Role"
                  otherClasses={{ container: 'w-full' }}
                  onSelect={(e) =>
                    formHandler?.setFieldValue('roleId', e, true)
                  }
                  errorMessage={
                    formHandler?.touched.roleId && formHandler?.errors.roleId
                      ? formHandler?.errors.roleId
                      : ''
                  }
                />
              </div>
            )}
          </div>
        )}

        <div className="my-6 flex w-full flex-col gap-4">
          <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-lg leading-6 font-bold">
            Personal Details
          </h6>
          <Input
            onBlur={formHandler?.handleBlur}
            label="Full Name*"
            errorMessage={
              formHandler?.touched.fullName && formHandler?.errors.fullName
                ? formHandler?.errors.fullName
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.fullName}
            type="text"
            placeholder="Write something here..."
            onChange={formHandler?.handleChange}
            name="fullName"
            otherClasses={{ container: 'w-full' }}
          />

          <div
            className={clsx(
              'grid',
              (isCurrentUser && !isOwner) ||
                (!isCurrentUser && isOwner) ||
                (!isCurrentUser && !isOwner)
                ? 'grid-cols-1 gap-6 md:grid-cols-2'
                : 'grid-cols-1',
            )}
          >
            <DatePicker
              otherClasses={{ container: 'w-full' }}
              label="Date of Birth*"
              name="date"
              startMonth={moment([1950, 0]).toDate()}
              endMonth={moment().toDate()}
              // endMonth={moment().subtract(18, 'years').toDate()}
              value={String(formHandler?.values?.dob)}
              placeholder="Select date of birth"
              onBlur={() => {
                if (formHandler?.values?.dob) {
                  formHandler?.setFieldTouched('dob', true);
                }
              }}
              disabled={formHandler.isSubmitting}
              onChange={(e) => {
                formHandler?.setFieldValue('dob', e, true);
              }}
              errorMessage={
                formHandler?.touched.dob && formHandler?.errors.dob
                  ? formHandler?.errors.dob
                  : ''
              }
            />
            {((isCurrentUser && !isOwner) ||
              (!isCurrentUser && isOwner) ||
              (!isCurrentUser && !isOwner)) && (
              <DatePicker
                otherClasses={{ container: 'w-full' }}
                label="Date of Hire*"
                name="date"
                startMonth={moment([1950, 0]).toDate()}
                endMonth={moment().toDate()}
                // startMonth={
                //   formHandler?.values?.dob
                //     ? moment(formHandler.values.dob).add(18, 'years').toDate()
                //     : new Date(1950, 0)
                // }
                // endMonth={new Date()}
                value={String(formHandler?.values?.dateOfHire)}
                placeholder="Select date of hire"
                // onBlur={formHandler.handleBlur}
                onBlur={() => {
                  if (formHandler?.values?.dateOfHire) {
                    formHandler?.setFieldTouched('dateOfHire', true);
                  }
                }}
                disabled={formHandler.isSubmitting}
                onChange={(e) => {
                  formHandler?.setFieldValue('dateOfHire', e, true);
                }}
                errorMessage={
                  formHandler?.touched.dateOfHire &&
                  formHandler?.errors.dateOfHire
                    ? formHandler?.errors.dateOfHire
                    : ''
                }
              />
            )}
          </div>
          <Input
            onBlur={formHandler.handleBlur}
            label="Contact Number*"
            errorMessage={
              formHandler?.touched.contact && formHandler?.errors.contact
                ? formHandler?.errors.contact
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={String(formHandler.values.contact)}
            type="tel"
            placeholder="Enter contact number"
            onChange={formHandler.handleChange}
            name="contact"
            otherClasses={{ container: 'w-full' }}
          />
        </div>
        {((isCurrentUser && !isOwner) ||
          (!isCurrentUser && isOwner) ||
          (!isCurrentUser && !isOwner)) && (
          <div className="flex w-full flex-col gap-4">
            <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-lg leading-6 font-bold">
              Designation
            </h6>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Select
                label="Department*"
                list={updatedDepartments}
                value={String(formHandler.values.departmentId)}
                disabled={formHandler.isSubmitting}
                placeholder="Departments"
                otherClasses={{
                  container: 'w-full',
                  listContainer: 'top-auto bottom-69',
                }}
                onSelect={(e) =>
                  formHandler?.setFieldValue('departmentId', e, true)
                }
                errorMessage={
                  formHandler?.touched.departmentId &&
                  formHandler?.errors.departmentId
                    ? formHandler?.errors.departmentId
                    : ''
                }
              />
              <Input
                onBlur={formHandler?.handleBlur}
                label="Job Title*"
                errorMessage={
                  formHandler?.touched.jobTitle && formHandler?.errors.jobTitle
                    ? formHandler?.errors.jobTitle
                    : ''
                }
                disabled={formHandler?.isSubmitting}
                value={String(formHandler?.values.jobTitle)}
                type="text"
                placeholder="Write something here..."
                onChange={formHandler?.handleChange}
                name="jobTitle"
                otherClasses={{ container: 'w-full' }}
              />
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6">
          <Button
            variant="secondary"
            type="button"
            size={useButtonSizeSmall()}
            onClick={() => handleClose()}
            otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Discard Changes
          </Button>
          <Button
            type="submit"
            size={useButtonSizeSmall()}
            disable={!hasChanged}
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChangeUserRoleModal;
